import { useEffect, useState } from "react";

/**
 * Champ date au format français jj/mm/aaaa.
 * L'affichage est toujours en fr-FR (indépendant de la locale du navigateur),
 * la valeur remontée par `onChange` reste au format ISO `aaaa-mm-jj`.
 */
export function isoToFr(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

export function frToIso(fr: string): string {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(fr);
  if (!m) return "";
  const [, d, mo, y] = m;
  const date = new Date(`${y}-${mo}-${d}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  return `${y}-${mo}-${d}`;
}

function maskFr(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean);
  return parts.join("/");
}

type Props = {
  /** Valeur ISO `aaaa-mm-jj` (chaîne vide si non renseignée). */
  value: string;
  onChange: (iso: string) => void;
  label?: string;
  id?: string;
  className?: string;
  labelClassName?: string;
  required?: boolean;
  hideLabel?: boolean;
};

export function DateField({
  value,
  onChange,
  label = "Date de l'événement",
  id = "date-evenement",
  className = "",
  labelClassName = "",
  required = false,
  hideLabel = false,
}: Props) {
  const [text, setText] = useState(() => isoToFr(value));

  useEffect(() => {
    const next = isoToFr(value);
    if (frToIso(text) !== value) setText(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className="w-full">
      <label
        htmlFor={id}
        className={hideLabel ? "sr-only" : labelClassName || "block text-xs uppercase tracking-[0.2em] text-muted-foreground"}
      >
        {label}
      </label>
      <input
        id={id}
        type="text"
        lang="fr-FR"
        inputMode="numeric"
        autoComplete="off"
        required={required}
        placeholder="jj/mm/aaaa"
        pattern="\d{2}/\d{2}/\d{4}"
        maxLength={10}
        value={text}
        aria-label={label}
        onChange={(e) => {
          const masked = maskFr(e.target.value);
          setText(masked);
          onChange(frToIso(masked));
        }}
        className={className}
        data-clarity-mask="true"
      />
    </div>
  );
}
